import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, catchError, exhaustMap, filter, map, of, take, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GenericItemApiResponse, GenericListApiResponse } from '../_interface/apiResponse';
import { DeviceProfile, DeviceProfilePayload } from '../_interface/deviceProfile';

/**
 * Device-facing pristup profilima podešavanja (sve aplikacije: kiosk/waiter/kd/orders).
 * Auth = posedovanje udid cookie-ja (postavlja ga DeviceService / native shell) — bez tokena.
 * Pozivi idu na relativni `/api/` pa identično rade na centrali i iza LocalServer-a
 * (LocalServer služi MyProfile/Available iz svog keša i offline).
 *
 * Koji profil aplikacija koristi zna SAMO uređaj (od 19.10.2026): izbor se čuva lokalno, u
 * podešavanjima aplikacije (`appliedProfile`), a server ga ne beleži. Tok:
 * - izbor (ekran podešavanja, „Primeni profil" iz admina): `loadProfile(id)` → primeni;
 * - start aplikacije i WS 'deviceProfile.changed' za taj profil: `loadProfile(lokalni id)` → primeni
 *   (idempotentno po profileID+version); bez veze ostaje poslednja lokalna kopija.
 * Profil je DEFAULT — lokalne izmene na uređaju (override-i) uvek pobeđuju, osim kod push-a sa
 * resetOverrides.
 */
/**
 * Prvi start aplikacije (localStorage): 'pending' = čeka prvo povezivanje na firmu, 'done' = profil je
 * izabran (ili firma nije imala profil), odnosno instalacija je starija od ove odluke.
 */
const FIRST_RUN_KEY = 'deviceProfileFirstRun';

/**
 * Ključevi po kojima se prepoznaje instalacija koja je već radila — ⚠ SYNC: settings servisi aplikacija
 * (konobar čuva podešavanja pod `appSettings`, ostali pod `settings`).
 */
const EXISTING_INSTALL_KEYS = ['settings', 'appSettings', 'appliedProfile', 'settingsOverrides'];

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // bez storage-a (privatni režim) — prvi start se ponovi pri sledećem pokretanju
  }
}

@Injectable({
  providedIn: 'root'
})
export class DeviceProfileService {

  constructor(private http: HttpClient) {
  }

  /**
   * Profil po ID-u za primenu; `null` kao ID = bez profila (bez poziva servera). Rezultat null = profil
   * više ne postoji (obrisan) → aplikacija se vraća na svoja podešavanja. Greška (bez veze, profil
   * druge organizacije) → aplikacija zadržava poslednju lokalnu kopiju.
   */
  loadProfile(profileID: number | null): Observable<DeviceProfilePayload | null> {
    if (profileID === null) {
      return of(null);
    }
    const url = environment.apiBase + 'deviceProfile/forDevice?profileID=' + profileID;
    return this.http.get<GenericItemApiResponse<DeviceProfilePayload>>(url).pipe(
      map(r => { if (r.success) { return r.item || null; } throw r; })
    );
  }

  /**
   * Prvi start aplikacije: čim se prvi put poveže na firmu, sama izabere prvi profil te firme za svoju
   * aplikaciju (prvi u listi izbora), ako ga firma ima. Samo jednom po instalaciji — izbor „bez
   * profila" posle toga ostaje. Instalacija koja je već radila (ima svoja podešavanja ili profil) se
   * ne dira.
   *
   * `companyConnected` = server je potvrdio firmu konekcije (WS `setCompany`): tek tada je uređaj
   * vezan za firmu, pa server zna njene profile. Bez veze se pokušava na sledećem povezivanju.
   * Vraća profil za primenu (najviše jednom).
   */
  firstRunProfile(target: string, companyConnected: Observable<unknown>): Observable<DeviceProfilePayload> {
    let state = readStorage(FIRST_RUN_KEY);
    if (!state) {
      state = EXISTING_INSTALL_KEYS.some(key => readStorage(key) !== null) ? 'done' : 'pending';
      writeStorage(FIRST_RUN_KEY, state);
    }
    if (state !== 'pending') {
      return EMPTY;
    }
    return companyConnected.pipe(
      exhaustMap(() => this.getAvailable(target).pipe(
        exhaustMap(profiles => profiles.length ? this.loadProfile(profiles[0].profileID) : of(null)),
        map(payload => ({ payload })),
        catchError(() => EMPTY),
      )),
      take(1),
      tap(() => writeStorage(FIRST_RUN_KEY, 'done')),
      map(result => result.payload),
      filter((payload): payload is DeviceProfilePayload => !!payload),
    );
  }

  /** Stara dodela iz baze — samo za aplikacije koje još nisu prešle na `loadProfile` (POS, Delivery). */
  getMyProfile(): Observable<DeviceProfilePayload | null> {
    const url = environment.apiBase + 'deviceProfile/myProfile';
    return this.http.get<GenericItemApiResponse<DeviceProfilePayload>>(url).pipe(
      map(r => { if (r.success) { return r.item || null; } throw r; })
    );
  }

  /** Profili dostupni ovom uređaju za picker (filtrirano po targetu aplikacije). Bez settings sadržaja. */
  getAvailable(target: string): Observable<DeviceProfile[]> {
    const url = environment.apiBase + 'deviceProfile/availableForDevice?target=' + encodeURIComponent(target);
    return this.http.get<GenericListApiResponse<DeviceProfile>>(url).pipe(
      map(r => { if (r.success) { return r.items; } throw r; })
    );
  }

  /**
   * Stara dodela: uređaj upisuje izbor u bazu — samo za aplikacije koje još nisu prešle na
   * `loadProfile` (POS, Delivery).
   */
  selectMine(profileID: number | null): Observable<DeviceProfilePayload | null> {
    const url = environment.apiBase + 'deviceProfile/selectMine';
    return this.http.post<GenericItemApiResponse<DeviceProfilePayload>>(url, { profileID, dummy: '' }).pipe(
      map(r => { if (r.success) { return r.item || null; } throw r; })
    );
  }
}
